/**
 * The priority axis — how urgent a recommendation is, shared by the AI Audit Recommendations tab
 * and the Persona Recommendations tab.
 *
 * Lives here rather than beside the badge component so the maps can be imported without pulling in
 * a component, and so the two axes a card renders — priority and effort — sit side by side in
 * `utils/`. See [`./effort.ts`](./effort.ts) for the other half.
 */

/** The priority levels a recommendation card can carry, most urgent first. */
export type PriorityLevel = "Critical" | "High" | "Medium" | "Low";

/** The priority levels in display order, most urgent first. */
export const PRIORITY_LEVELS: PriorityLevel[] = ["Critical", "High", "Medium", "Low"];

/** The Priority filter's dropdown options, shared by every screen with a priority axis. */
export const PRIORITY_OPTIONS: { value: PriorityLevel; label: PriorityLevel }[] =
  PRIORITY_LEVELS.map((value) => ({ value, label: value }));

/** Pill styling for the "<level> Priority" badge. */
export const PRIORITY_BADGE_STYLES: Record<PriorityLevel, string> = {
  Critical: "bg-red-950 text-red-400",
  High: "bg-orange-900 text-orange-300",
  Medium: "bg-yellow-900 text-yellow-300",
  Low: "bg-blue-900 text-blue-300",
};

/**
 * Colour for the alert icon beside a card's title, matching the badge below it.
 *
 * Kept next to the badge styles so the icon and the badge cannot drift: they encode the same level.
 */
export const PRIORITY_ICON_COLORS: Record<PriorityLevel, string> = {
  Critical: "text-red-400",
  High: "text-orange-400",
  Medium: "text-yellow-500",
  Low: "text-blue-400",
};
